import { v14 } from '@readcloud/api-client';
import ConfigJson from '@readcloud/common/config.json';
import { Book, Cloud } from '@readcloud/data';
import {
  bookSelectors,
  cloudSelectors,
  cloudsActions,
  homeSettingsSelectors,
  useAppDispatch,
  usersSelectors,
  authSelectors,
} from '@readcloud/state';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloudsAndContent } from './CloudsAndContent';
import { HomeSettings } from '@readcloud/state';
import { CloudsAndContentAuthProvider } from './context/AuthContext';
import { CloudsAndContentActionsProvider } from './context/ActionsContext';
import { CloudsAndContentStateProvider } from './context/StateContext';
import { homeSettingsActions } from '@readcloud/state';
import { groupFilterSortClouds } from './helpers/cloudHelpers';
import { ConnectedToastGenerator } from './components/Toasts/ConnectedToastGenerator';
import { TooltipProvider } from '@readcloud/ui';
import { AuthContextUser } from '@readcloud/common';
import { QueryClient, QueryClientProvider } from 'react-query';

import { useUploadMutations } from './components/upload/hooks/useUploadMutations';

function ConnectedCloudsAndContent(props: {
  onBookClick: (book: Book, cloud: Cloud) => void;
  onLogout: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  onGotoContent: () => void;
  user: AuthContextUser;
}) {
  const clouds = useSelector(cloudSelectors.getClouds) || {};
  const userPermissions = useSelector(authSelectors.getUserPermissions);
  const permissions = useSelector(cloudSelectors.getPermissions) || {
    canAddCloud: false,
  };
  const books = useSelector(bookSelectors.getBooks) || [];

  const settings = useSelector(homeSettingsSelectors.getSettings) || [];
  const dispatch = useDispatch();
  const userList = useSelector(usersSelectors.getUsers) || [];

  const { updateContentMutation } = useUploadMutations(props.user);

  // Ensure settings is of type HomeSettings
  const typedSettings: HomeSettings = settings as HomeSettings;

  const uploadedBooks = React.useMemo(() => {
    if (!books || !props.user.id) return [];
    return books
      .filter((book) => book.owner === props.user.id)
      .sort((a, b) => b.updated.localeCompare(a.updated));
  }, [books, props.user?.id]);

  const orderedClouds = React.useMemo(() => {
    const items = groupFilterSortClouds(
      books,
      Object.values(clouds),
      typedSettings
    );
    return items;
  }, [
    books,
    Object.values(clouds),
    typedSettings.myInteractiveLoginsPosition,
    typedSettings.myPurchasesCloudPosition,
    typedSettings.layout,
    typedSettings.hideTitle,
    typedSettings.hideEmptyClouds,
  ]);

  React.useEffect(() => {
    if (userPermissions?.['institutionClouds']?.read) {
      dispatch(cloudsActions.asyncActions.getInstitutionClouds({}) as any);
    }
  }, [dispatch, userPermissions?.['institutionClouds']?.read]);

  const refreshAll = React.useCallback(() => {
    Promise.allSettled([
      // refresh that should happen for every user.
      props.onRefresh(),
      // refresh that should happen for users with U_PopulateSchoolClouds.
      userPermissions?.['institutionClouds']?.read &&
        dispatch(cloudsActions.asyncActions.getInstitutionClouds({}) as any),
    ]);
  }, [dispatch, props.onRefresh, props.user.userScope]);

  return (
    <CloudsAndContentAuthProvider auth={props.user}>
      <CloudsAndContentActionsProvider
        gotoContent={props.onGotoContent}
        tileClick={props.onBookClick}
        logout={props.onLogout}
        updateSettings={(settings: Partial<HomeSettings>) =>
          dispatch(homeSettingsActions.updateHomeSettings(settings))
        }
        createCloud={(data) =>
          (dispatch as any)(cloudsActions.asyncActions.addCloud(data)).then(
            ({ payload }) => payload
          )
        }
        updateCloud={(data) =>
          (dispatch as any)(cloudsActions.asyncActions.updateCloud(data)).then(
            ({ payload }) => payload
          )
        }
        deleteCloud={(cloudId) =>
          (dispatch as any)(cloudsActions.asyncActions.deleteCloud(cloudId))
        }
        refresh={refreshAll}
        uploadFile={v14.App.uploadFile}
        removeBookFromCloud={async ({ cloudId, book }) => {
          const payload = {
            ...book,
            cloudAction: 'delete',
            cloudIds: [cloudId],
          };

          return await updateContentMutation.mutateAsync(payload);
        }}
      >
        <CloudsAndContentStateProvider
          permissions={permissions}
          version={ConfigJson.WebAppVersion}
        >
          <TooltipProvider>
            <CloudsAndContent
              books={books}
              myUploads={uploadedBooks}
              clouds={orderedClouds}
              user={props.user}
              settings={typedSettings}
              userList={userList || []}
              refreshing={props.refreshing}
            />
            <ConnectedToastGenerator timeout={10000} />
          </TooltipProvider>
        </CloudsAndContentStateProvider>
      </CloudsAndContentActionsProvider>
    </CloudsAndContentAuthProvider>
  );
}

const WrappedConnectedCloudsAndContent = (props: {
  onBookClick: (book: Book, cloud: Cloud) => void;
  onLogout: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  onGotoContent: () => void;
  user: AuthContextUser;
}) => {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <ConnectedCloudsAndContent {...props} />
    </QueryClientProvider>
  );
};

export { WrappedConnectedCloudsAndContent as ConnectedCloudsAndContent };
